import { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";

import SoftBox from "components/SoftBox";
import SoftSelect from "components/SoftSelect";
import SoftTypography from "components/SoftTypography";
import { API_URL } from "config";

const toOption = (row) => ({
  ...row,
  value: Number(row.id),
  label: row.name,
});

function MasterLocationSelect({
  countryId,
  countryName,
  stateId,
  stateName,
  onCountryChange,
  onStateChange,
  disabled,
  showCountry,
  showState,
}) {
  const [countries, setCountries] = useState([]);
  const [states, setStates] = useState([]);
  const [countryLoading, setCountryLoading] = useState(false);
  const [stateLoading, setStateLoading] = useState(false);
  const headers = useMemo(() => ({
    Authorization: `Bearer ${localStorage.getItem("authToken")}`,
    Accept: "application/json",
  }), []);

  useEffect(() => {
    let active = true;
    setCountryLoading(true);
    fetch(`${API_URL}/client-countries`, { headers })
      .then((response) => response.json().then((data) => ({ response, data })))
      .then(({ response, data }) => {
        if (!active || !response.ok) return;
        setCountries((Array.isArray(data.data) ? data.data : []).map(toOption));
      })
      .catch(() => {
        if (active) setCountries([]);
      })
      .finally(() => {
        if (active) setCountryLoading(false);
      });
    return () => {
      active = false;
    };
  }, [headers]);

  useEffect(() => {
    let active = true;
    if (!countryId) {
      setStates([]);
      return () => {
        active = false;
      };
    }
    setStateLoading(true);
    fetch(
      `${API_URL}/client-states?client_country_id=${encodeURIComponent(countryId)}`,
      { headers }
    )
      .then((response) => response.json().then((data) => ({ response, data })))
      .then(({ response, data }) => {
        if (!active || !response.ok) return;
        setStates((Array.isArray(data.data) ? data.data : []).map(toOption));
      })
      .catch(() => {
        if (active) setStates([]);
      })
      .finally(() => {
        if (active) setStateLoading(false);
      });
    return () => {
      active = false;
    };
  }, [countryId, headers]);

  const selectedCountry = countries.find((option) => Number(option.value) === Number(countryId))
    || countries.find((option) => option.name === countryName)
    || (countryName ? { value: countryId || countryName, label: countryName, name: countryName } : null);
  const selectedState = states.find((option) => Number(option.value) === Number(stateId))
    || states.find((option) => option.name === stateName)
    || (stateName ? { value: stateId || stateName, label: stateName, name: stateName } : null);

  return (
    <>
      {showCountry && (
        <SoftBox mb={0.7}>
          <SoftTypography variant="caption">Country</SoftTypography>
          <SoftSelect
            options={countries}
            value={selectedCountry}
            isLoading={countryLoading}
            isDisabled={disabled}
            isSearchable
            isClearable
            placeholder="Select country"
            onChange={(option) => {
              onCountryChange(option
                ? { id: Number(option.id || option.value), name: option.name || option.label }
                : { id: null, name: "" });
              onStateChange({ id: null, name: "" });
            }}
          />
        </SoftBox>
      )}
      {showState && (
        <SoftBox mb={0.7}>
          <SoftTypography variant="caption">State</SoftTypography>
          <SoftSelect
            options={states}
            value={selectedState}
            isLoading={stateLoading}
            isDisabled={disabled || !countryId}
            isSearchable
            isClearable
            placeholder={countryId ? "Select state" : "Select country first"}
            onChange={(option) => onStateChange(option
              ? { id: Number(option.id || option.value), name: option.name || option.label }
              : { id: null, name: "" })}
          />
        </SoftBox>
      )}
    </>
  );
}

MasterLocationSelect.defaultProps = {
  countryId: null,
  countryName: "",
  stateId: null,
  stateName: "",
  disabled: false,
  showCountry: true,
  showState: true,
};

MasterLocationSelect.propTypes = {
  countryId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  countryName: PropTypes.string,
  stateId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  stateName: PropTypes.string,
  onCountryChange: PropTypes.func.isRequired,
  onStateChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  showCountry: PropTypes.bool,
  showState: PropTypes.bool,
};

export default MasterLocationSelect;
